import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PushNotificationsProvider } from '../../providers/push-notifications/push-notifications';

@Component({
  selector: 'app-push-notifications',
  templateUrl: './push-notifications.component.html',
  styleUrls: ['./push-notifications.component.scss'],
})
export class PushNotificationsComponent implements OnInit {
  contexts = [
    { value: 'st', label: 'SuperTreinos' },
    { value: 'sc', label: 'ST Coach Aluno' },
    { value: 'sct', label: 'ST Coach Professor' },
    { value: 'homolog', label: 'Homologação' },
  ];
  audienceTypes = [
    { value: 'external_ids', label: 'External IDs' },
    { value: 'customers', label: 'IDs de clientes' },
    { value: 'teachers', label: 'IDs de professores' },
  ];
  automations: any[] = [];
  campaigns: any[] = [];
  deliveries: any[] = [];
  subscriber: any = null;
  loading = { automations: false, campaigns: false, history: false, subscriber: false, test: false, campaign: false };
  context = this.contexts[0].value;
  testForm: any = { audience_type: 'external_ids', recipient: '', title: '', message: '' };
  campaignForm: any = { name: '', title: '', message: '', audience_type: 'external_ids', recipients: '' };
  historyFilters: any = { recipient: '', flag: '' };
  historyPagination: any = { page: 1, perPage: 50, total: 0, lastPage: 1, from: 0, to: 0 };

  constructor(private push: PushNotificationsProvider, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.loadAutomations();
    this.loadCampaigns();
    this.loadHistory();
  }

  async loadAutomations(): Promise<void> {
    this.loading.automations = true;
    try {
      const response: any = await this.push.automations(this.context);
      this.automations = response.automations || [];
    } catch (error) {
      this.showError(error, 'Não foi possível carregar as automações.');
    } finally {
      this.loading.automations = false;
    }
  }

  async toggleAutomation(automation: any): Promise<void> {
    const enabled = !!automation.enabled;
    automation.saving = true;
    try {
      const response: any = await this.push.updateAutomation(automation.key, enabled);
      automation.enabled = response.automation.enabled;
      this.toastr.success('Automação atualizada.');
    } catch (error) {
      automation.enabled = !enabled;
      this.showError(error, 'Não foi possível atualizar a automação.');
    } finally {
      automation.saving = false;
    }
  }

  async inspectSubscriber(): Promise<void> {
    const recipient = (this.testForm.recipient || '').trim();
    if (!recipient) {
      this.toastr.warning('Informe o external ID.');
      return;
    }

    this.loading.subscriber = true;
    this.subscriber = null;
    try {
      const response: any = await this.push.subscriber(recipient, this.context);
      this.subscriber = response.data;
    } catch (error) {
      this.showError(error, 'Subscriber não encontrado no contexto selecionado.');
    } finally {
      this.loading.subscriber = false;
    }
  }

  async sendTest(): Promise<void> {
    if (!this.validTestForm()) { return; }
    if (!window.confirm(`Enviar uma notificação de teste somente para ${this.testForm.recipient}?`)) { return; }

    this.loading.test = true;
    try {
      const response: any = await this.push.sendTest({
        app_context: this.context,
        audience_type: this.testForm.audience_type,
        recipient: this.testForm.recipient.trim(),
        title: this.testForm.title.trim(),
        message: this.testForm.message.trim(),
        locale: 'pt',
      });
      const delivery = response.delivery || {};
      this.toastr.success(`Envio registrado como ${delivery.status || 'aceito'}.`);
      await this.loadHistory();
    } catch (error) {
      this.showError(error, 'O OneSignal recusou o envio de teste.');
      await this.loadHistory();
    } finally {
      this.loading.test = false;
    }
  }

  async loadCampaigns(): Promise<void> {
    this.loading.campaigns = true;
    try {
      const response: any = await this.push.campaigns({ per_page: 30, app_context: this.context });
      this.campaigns = response.data || [];
    } catch (error) {
      this.showError(error, 'Não foi possível carregar as campanhas.');
    } finally {
      this.loading.campaigns = false;
    }
  }

  async createCampaign(): Promise<void> {
    const recipients = this.parseRecipients(this.campaignForm.recipients);
    if (!this.campaignForm.name.trim() || !this.campaignForm.title.trim() || !this.campaignForm.message.trim() || !recipients.length) {
      this.toastr.warning('Preencha nome, título, mensagem e destinatários.');
      return;
    }

    this.loading.campaign = true;
    try {
      await this.push.createCampaign({
        name: this.campaignForm.name.trim(),
        title: this.campaignForm.title.trim(),
        message: this.campaignForm.message.trim(),
        locale: 'pt',
        audience_type: this.campaignForm.audience_type,
        recipients,
        app_context: this.context,
      });
      this.campaignForm = { name: '', title: '', message: '', audience_type: 'external_ids', recipients: '' };
      this.toastr.success('Campanha criada como rascunho.');
      await this.loadCampaigns();
    } catch (error) {
      this.showError(error, 'Não foi possível criar a campanha.');
    } finally {
      this.loading.campaign = false;
    }
  }

  async sendCampaign(campaign: any): Promise<void> {
    const recipients = (campaign.audience_snapshot || []).length;
    if (!window.confirm(`Enviar “${campaign.name}” para ${recipients} destinatário(s)? Esta ação não pode ser desfeita.`)) { return; }

    campaign.sending = true;
    try {
      await this.push.sendCampaign(campaign.id);
      this.toastr.success('Campanha processada.');
    } catch (error) {
      this.showError(error, 'A campanha terminou com falhas. Consulte o histórico.');
    } finally {
      campaign.sending = false;
      await Promise.all([this.loadCampaigns(), this.loadHistory()]);
    }
  }

  async loadHistory(page: number = this.historyPagination.page): Promise<void> {
    this.loading.history = true;
    try {
      const params: any = {
        per_page: this.historyPagination.perPage,
        page,
        app_context: this.context,
      };
      const recipient = (this.historyFilters.recipient || '').trim();
      const flag = (this.historyFilters.flag || '').trim();
      if (recipient) {
        params.recipient = recipient;
      }
      if (flag) {
        params.flag = flag;
      }

      const response: any = await this.push.history(params);
      this.deliveries = response.data || [];
      this.historyPagination.page = response.current_page || page;
      this.historyPagination.perPage = response.per_page || this.historyPagination.perPage;
      this.historyPagination.total = response.total || 0;
      this.historyPagination.lastPage = response.last_page || 1;
      this.historyPagination.from = response.from || 0;
      this.historyPagination.to = response.to || 0;
    } catch (error) {
      this.showError(error, 'Não foi possível carregar o histórico.');
    } finally {
      this.loading.history = false;
    }
  }

  applyHistoryFilters(): Promise<void> {
    return this.loadHistory(1);
  }

  clearHistoryFilters(): Promise<void> {
    this.historyFilters = { recipient: '', flag: '' };
    return this.loadHistory(1);
  }

  previousHistoryPage(): Promise<void> {
    if (this.historyPagination.page <= 1) { return Promise.resolve(); }
    return this.loadHistory(this.historyPagination.page - 1);
  }

  nextHistoryPage(): Promise<void> {
    if (this.historyPagination.page >= this.historyPagination.lastPage) { return Promise.resolve(); }
    return this.loadHistory(this.historyPagination.page + 1);
  }

  async syncDelivery(delivery: any): Promise<void> {
    if (!delivery.onesignal_notification_id) { return; }
    delivery.syncing = true;
    try {
      const response: any = await this.push.syncDelivery(delivery.id, this.context);
      Object.assign(delivery, response.delivery || {});
      this.toastr.success('Métricas sincronizadas.');
    } catch (error) {
      this.showError(error, 'Não foi possível sincronizar a entrega.');
    } finally {
      delivery.syncing = false;
    }
  }

  statusLabel(status: string): string {
    const labels: any = { draft: 'Rascunho', processing: 'Processando', scheduled: 'Agendada', sent: 'Enviada', partial: 'Parcial', failed: 'Falhou', skipped: 'Ignorada' };
    return labels[status] || status || 'Pendente';
  }

  changeContext(): void {
    this.subscriber = null;
    this.historyFilters.flag = '';
    this.historyPagination.page = 1;
    this.loadAutomations();
    this.loadCampaigns();
    this.loadHistory(1);
  }

  private validTestForm(): boolean {
    if (!this.testForm.recipient.trim() || !this.testForm.title.trim() || !this.testForm.message.trim()) {
      this.toastr.warning('Preencha destinatário, título e mensagem.');
      return false;
    }
    return true;
  }

  private parseRecipients(value: string): string[] {
    return Array.from(new Set((value || '').split(/[\s,;]+/).map(item => item.trim()).filter(Boolean)));
  }

  private showError(error: any, fallback: string): void {
    const body = error && error.error ? error.error : {};
    this.toastr.error(body.err || body.error_message || fallback);
  }
}
